import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getNextRotation, getRotationTransform } from './videoRotationUtils'

/**
 * Manages 90°-step rotation for a scan video.
 *
 * Tracks the current angle and watches the rendered frame size, so the
 * 90°/270° scale-to-fit factor stays correct as the scan card resizes.
 *
 * @returns {{
 *   rotate: () => void,
 *   transform: string,
 *   frameRef: (node: (HTMLElement | null)) => void
 * }} Attach `frameRef` to the element wrapping the video so its size can be
 * measured; apply `transform` as the video's CSS `transform`.
 */
const useVideoRotation = () => {
  const [rotation, setRotation] = useState(0)
  const [frameSize, setFrameSize] = useState({ width: 0, height: 0 })
  const observerRef = useRef(null)

  const frameRef = useCallback(node => {
    observerRef.current?.disconnect()
    observerRef.current = null

    if (!node || typeof ResizeObserver === 'undefined') {
      return
    }

    observerRef.current = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      setFrameSize({ width, height })
    })
    observerRef.current.observe(node)
  }, [])

  useEffect(() => () => observerRef.current?.disconnect(), [])

  const rotate = useCallback(() => setRotation(getNextRotation), [])

  const transform = useMemo(
    () => getRotationTransform(rotation, frameSize.width, frameSize.height),
    [rotation, frameSize.width, frameSize.height]
  )

  return { rotate, transform, frameRef }
}

export default useVideoRotation
